import React, { useState, useEffect } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Font
} from "@react-pdf/renderer";

Font.registerHyphenationCallback((word) => [word]);

// Definir estilos para el documento PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 20,
  },
  title: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: "center",
    color: "#333",
  },
  section: {
    marginBottom: 20,
    padding: 10,
  },
  tableTitle: {
    fontSize: 16,
    marginBottom: 10,
    textAlign: "left",
    fontWeight: "bold",
  },
  tableSubTitle: {
    fontSize: 12,
    marginBottom: 10,
    textAlign: "left",
    fontWeight: "bold",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 5,
    overflow: "hidden",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    flex: 1, // Cada columna tiene el mismo ancho
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "#f0f0f0",
    paddingVertical: 4,     // <- menos padding
    paddingHorizontal: 3,
    justifyContent: "center",
  },
  tableCol: {
    flex: 1, // Cada columna tiene el mismo ancho
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#ddd",
    padding: 8,
  },
  tableCellHeader: {
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center",
  },
  tableCell: {
    fontSize: 10,
    textAlign: "center",
  },
  footerRow: {
    backgroundColor: "#d4edda", // Color de fondo para el pie de tabla
    flexDirection: "row",
  },
  footerCell: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 10,
    padding: 8,
    flex: 1, // Cada celda del footer tiene el mismo ancho
  },
  footerCellEnd: {
    textAlign: "right",
    fontWeight: "bold",
    fontSize: 10,
    padding: 8,
    flex: 7, // Simula colspan
  },
  strikethrough: {
    textDecoration: "line-through",
    color: "red", // Color opcional para el tachado
  },
  boldText: {
    fontWeight: "bold",
  },
  signatureSection: {
    position: "absolute",
    bottom: 40,
    left: 20,
    right: 20,
    paddingTop: 10,
    fontSize: 12,
    textAlign: "center", // C
  },
  periodText: {
    fontSize: 12,
    marginBottom: 15,
    textAlign: "center",
    color: "#333",
  },
});

const splitReason = (reason) => {
  const raw = (reason ?? '').trim();
  if (!raw) return { concept: '', subconcept: '', detail: '' };
  const parts = raw.split('/').map(p => p.trim()).filter(Boolean);
  return {
    concept: parts[0] ?? '',
    subconcept: parts[1] ?? '',
    detail: parts.slice(2).join(' / ') ?? ''
  };
};

const resolveConceptCols = (subItem) => {
  const hasNormalized =
    (subItem?.concept && String(subItem.concept).trim() !== '') ||
    (subItem?.subconcept && String(subItem.subconcept).trim() !== '') ||
    (subItem?.detail && String(subItem.detail).trim() !== '');

  if (hasNormalized) {
    return {
      concept: (subItem.concept ?? '').toString().trim() || '-',
      subconcept: (subItem.subconcept ?? '').toString().trim() || 'N/A',
      detail: (subItem.detail ?? '').toString().trim() || '-',
    };
  }

  const rr = splitReason(subItem?.reason);
  return {
    concept: rr.concept || '-',
    subconcept: rr.subconcept || 'N/A',
    detail: rr.detail || '-',
  };
};

// Crear el documento PDF utilizando componentes de @react-pdf/renderer
const MyReport = ({ data, periodLabel }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>Reporte de tiempo extra</Text>
      {periodLabel ? (<Text style={styles.periodText}>Periodo: {periodLabel}</Text>) : null}
      <View style={styles.section}>
        <Text style={styles.tableTitle}>Resumen</Text>
        <View style={styles.table}>
          {/* Encabezados de la tabla */}
          <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
              <Text style={styles.tableCellHeader}>Nombre</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.tableCellHeader}>Cantidad</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.tableCellHeader}>Precio</Text>
            </View>
          </View>
          {/* Filas de datos de la tabla */}
          {data.requests.map((item) => (
            <View style={styles.tableRow} key={`summary-${item.id}`}>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{item.user}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{item.hoursToPayTotal}</Text>
              </View>
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                  {(item.modifiedTotal !== null && item.modifiedTotal !== 0) && (
                    <Text style={styles.strikethrough}>
                      ${item.totalToPay}
                    </Text>
                  )}
                  <Text style={styles.boldText}>
                    ${item.modifiedTotal === null ? item.totalToPay : item.modifiedTotal}
                  </Text>
                </Text>
              </View>
            </View>
          ))}
          {/* Pie de tabla */}
          <View style={[styles.footerRow]}>
            <View style={styles.footerCell}>
              <Text>Totales:</Text>
            </View>
            <View style={styles.footerCell}>
              <Text>{data.totalHours}</Text>
            </View>
            <View style={styles.footerCell}>
              <Text>${data.totalToPay}</Text>
            </View>
          </View>
        </View>
      </View>
    </Page>
    {data.requests.map((item) => (
      <Page size="A4" style={styles.page} key={`detail-${item.id}`}>
        <Text style={styles.title}>{item.user}</Text>
        <View style={styles.section}>
          <View style={styles.table}>
            {/* Encabezados de la tabla */}
            <View style={styles.tableRow}>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Fecha</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Concepto</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Subconcepto</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Detalle</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Trabajo desde casa</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Horas solicitadas</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Horas a pagar</Text>
              </View>
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Total a pagar por día</Text>
              </View>
            </View>
            {item.items.map((subItem, subIndex) => (
              <View style={styles.tableRow} key={`item-${subItem.id}-${subIndex}`}>
                {(() => {
                  const cols = resolveConceptCols(subItem);
                  return (
                    <>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{subItem.date} ({subItem.dayOfTheWeek})</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{cols.concept}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{cols.subconcept}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{cols.detail}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{subItem.itsWorkFromHome ? "Sí" : "No"}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{subItem.hours}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>{subItem.hoursToPay}</Text>
                      </View>
                      <View style={styles.tableCol}>
                        <Text style={styles.tableCell}>
                          {/* Texto tachado si es necesario */}
                          {(subItem.modifiedPayment !== null && subItem.modifiedPayment !== 0) && (
                            <Text style={styles.strikethrough}>${subItem.moneyToPay} </Text>
                          )}
                          {/* Texto en negrita */}
                          <Text style={styles.boldText}>
                            ${subItem.modifiedPayment === null ? subItem.moneyToPay : subItem.modifiedPayment}
                          </Text>
                        </Text>
                      </View>
                    </>
                  );
                })()}
              </View >
            ))}
            {(item.formattedExtraPayments ?? []).map((subItem, subIndex) => (
              <View style={styles.tableRow} key={`extra-${subItem.id}-${subIndex}`}>
                {/* 1) Fecha */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{subItem.formattedDate}</Text>
                </View>

                {/* 2) Concepto */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>-</Text>
                </View>

                {/* 3) Subconcepto */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>N/A</Text>
                </View>

                {/* 4) Detalle */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>{subItem.reason ?? '-'}</Text>
                </View>

                {/* 5) Trabajo desde casa */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>-</Text>
                </View>

                {/* 6) Horas solicitadas */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>-</Text>
                </View>

                {/* 7) Horas a pagar */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>-</Text>
                </View>

                {/* 8) Total a pagar por día */}
                <View style={styles.tableCol}>
                  <Text style={styles.tableCell}>
                    <Text style={styles.boldText}>$ {subItem.pay}</Text>
                  </Text>
                </View>
              </View>
            ))}
            <View style={[styles.footerRow]}>
              <View style={styles.footerCellEnd}>
                <Text>Total a pagar:</Text>
              </View>
              <View style={styles.footerCell}>
                <Text>{item.hoursToPayTotal}</Text>
              </View>
              <View style={styles.footerCell}>
                <Text>${item.modifiedTotal === null ? item.totalToPay : item.modifiedTotal}</Text>
              </View>
            </View>
          </View>
        </View>
        <Text style={styles.tableSubTitle}>¿Quien creo la solicitud?: {item.requester}</Text>
        <Text style={styles.tableSubTitle}>Primer aprobador: {item.approver}</Text>
        <Text style={styles.tableSubTitle}>Periodo: {item.startDate} - {item.endDate}</Text>
        <View style={styles.signatureSection}>
          <Text>______________________________________________________________</Text>
          <Text>Firma de recibido ({item.user})</Text>
        </View>
      </Page>
    ))}
  </Document>
);

// Crear un componente para proporcionar el enlace de descarga del PDF
const PDFLink = ({ data, periodLabel }) => (
  <div style={{ textAlign: "center", marginTop: "20px" }}>
    <PDFDownloadLink
      document={<MyReport data={data} periodLabel={periodLabel} />}
      fileName="Reporte-Solicitudes-Pago.pdf"
    >
      {({ blob, url, loading, error }) =>
        loading ? "Generando documento..." : "Descargar PDF"
      }
    </PDFDownloadLink>
  </div>
);

// Al final de Report.js, asegúrate de exportar el componente PDFLink
export { PDFLink };